import {
  CondensedCustomPdfAppearance,
  defaultCondensedCustomPdfAppearanceValues,
} from "@ac-expo/api-types";
import { GState, jsPDF } from "jspdf";
import { PDFDocument, PDFFont, PDFImage, PDFPage, StandardFonts, rgb } from "pdf-lib";
import { hexaToRgb } from "../utils";
import {
  FOUR_QUARTERS_PDF_LAYOUT,
  LINE_PDF_LAYOUT,
} from "./pdf-generation.constants";

declare global {
  interface Window {
    html2canvas: any;
  }
}

const conditionsTextPart1 =
  "Ce billet est soumis aux conditions générales de\n" +
  "vente et d’utilisation acceptées lors de l’achat\n" +
  "ou de l'inscription. Toute reproduction ou revente\n" +
  "est interdite. L’accès pourra être refusé en cas\n" +
  "de non-respect. Les CGV et CGU complètes sont\n" +
  "disponibles sur http://www.connect-events.fr .";

const displayPictureFill = (
  doc: jsPDF,
  image: string,
  minX: number,
  minY: number,
  maxWidth: number,
  maxHeight: number
) => {
  const imgProps = doc.getImageProperties(image);
  const ratioX = maxWidth / imgProps.width;
  const ratioY = maxHeight / imgProps.height;
  if (ratioX > ratioY) {
    const ratio = ratioX;
    const height = imgProps.height * ratio;
    const width = imgProps.width * ratio;
    const x = (maxWidth - width) / 2 + minX;
    const y = (maxHeight - height) / 2 + minY;
    doc.addImage(image, "PNG", x, y, width, height);
  } else {
    const ratio = ratioY;
    const width = imgProps.width * ratio;
    const height = imgProps.height * ratio;
    const x = (maxWidth - width) / 2 + minX;
    const y = (maxHeight - height) / 2 + minY;
    doc.addImage(image, "PNG", x, y, width, height);
  }
};

const displayPictureLimited = (
  doc: jsPDF,
  image: string,
  minX: number,
  minY: number,
  maxWidth: number,
  maxHeight: number
) => {
  const imgProps = doc.getImageProperties(image);
  const ratioX = maxWidth / imgProps.width;
  const ratioY = maxHeight / imgProps.height;
  const ratio = ratioX < ratioY ? ratioX : ratioY;
  const width = imgProps.width * ratio;
  const height = imgProps.height * ratio;
  const x = (maxWidth - width) / 2 + minX;
  const y = (maxHeight - height) / 2 + minY;
  doc.addImage(image, "PNG", x, y, width, height);
};

const pdfGenerationQrCodeQuarter = async (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  customAppearance: CondensedCustomPdfAppearance,
  informations: string,
  eventName: string,
  qrCodeImage: string | null,
  qrCodeId: string | null,
  logoImage: string,
  customImage?: string
) => {
  const currentHeaderColor = hexaToRgb(customAppearance.headerColor);
  const currentTextColor = hexaToRgb(customAppearance.textColor);
  doc.setFontSize(17);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(
    currentHeaderColor.r,
    currentHeaderColor.g,
    currentHeaderColor.b
  );
  if (customImage) {
    displayPictureLimited(doc, customImage, x, y, width, (width / 174) * 36);
  } else {
    const ticketNameLines = doc.splitTextToSize(eventName, width - 40);
    doc.text(ticketNameLines, x + width / 2, y + 60, { align: "center" });
  }

  doc.setTextColor(currentTextColor.r, currentTextColor.g, currentTextColor.b);
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "normal");
  doc.setLineHeightFactor(1);
  const quarterCard = FOUR_QUARTERS_PDF_LAYOUT.card;
  await doc.html(informations, {
    x: x + (width - quarterCard.width) / 2,
    y: y + quarterCard.htmlOriginY,
    width: quarterCard.htmlTargetWidth,
  });
  doc.setFont("helvetica", "italic");
  doc.text("Propulsé par", x + 115, y + 245, {
    align: "center",
    maxWidth: 200,
  });
  doc.setFont("helvetica", "normal");
  doc.addImage(logoImage, "PNG", x + 148, y + 237, 64, 12);
};

const pdfGenerationQrCodeLine = async (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  customAppearance: CondensedCustomPdfAppearance,
  informations: string,
  eventName: string,
  qrCodeImage: string | null,
  qrCodeId: string | null,
  logoImage: string,
  customImage?: string
) => {
  const currentHeaderColor = hexaToRgb(customAppearance.headerColor);
  const currentTextColor = hexaToRgb(customAppearance.textColor);
  doc.setFontSize(17);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(
    currentHeaderColor.r,
    currentHeaderColor.g,
    currentHeaderColor.b
  );
  if (customImage) {
    displayPictureLimited(doc, customImage, x, y, width, (width / 420) * 72);
  } else {
    doc.text(eventName, x + width / 2, y + 60, { align: "center" });
  }
  doc.setTextColor(currentTextColor.r, currentTextColor.g, currentTextColor.b);
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "normal");
  doc.setLineHeightFactor(1);
  const lineCard = LINE_PDF_LAYOUT.card;
  await doc.html(informations, {
    x: x + lineCard.htmlOriginX,
    y: y + lineCard.htmlOriginY,
    width: lineCard.htmlTargetWidth,
  });
  doc.setFont("helvetica", "italic");
  doc.text("Propulsé par", x + 97, y + 255, {
    align: "center",
    maxWidth: 200,
  });
  doc.setFont("helvetica", "normal");
  doc.addImage(logoImage, "PNG", x + 130, y + 247, 64, 12);
};

const pdfGenerationConditionsQuarter = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  customAppearance: CondensedCustomPdfAppearance
) => {
  const currentHeaderColor = hexaToRgb(customAppearance.headerColor);
  const currentTextColor = hexaToRgb(customAppearance.textColor);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(
    currentHeaderColor.r,
    currentHeaderColor.g,
    currentHeaderColor.b
  );
  const conditionsTitle = "CONDITIONS GÉNÉRALES D'UTILISATION";
  doc.text(conditionsTitle, x + 15, y + 30, {
    align: "left",
    maxWidth: width - 30,
  });
  doc.setTextColor(currentTextColor.r, currentTextColor.g, currentTextColor.b);
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setLineHeightFactor(1);

  doc.text(conditionsTextPart1, x + 15, y + 50, {
    align: "left",
    maxWidth: width - 30,
  });
};

const pdfGenerationConditionsLine = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  customAppearance: CondensedCustomPdfAppearance
) => {
  const currentHeaderColor = hexaToRgb(customAppearance.headerColor);
  const currentTextColor = hexaToRgb(customAppearance.textColor);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(
    currentHeaderColor.r,
    currentHeaderColor.g,
    currentHeaderColor.b
  );
  const conditionsTitle = "CONDITIONS GÉNÉRALES D'UTILISATION";
  doc.text(conditionsTitle, x + 15, y + 30, {
    align: "left",
    maxWidth: width - 30,
  });
  doc.setTextColor(currentTextColor.r, currentTextColor.g, currentTextColor.b);
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setLineHeightFactor(1);

  doc.text(conditionsTextPart1, x + 15, y + 50, {
    align: "left",
    maxWidth: width / 2 - 30,
  });
};

const pdfGenerationCustomSectionQuarter = async (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  customSection: string | null
) => {
  doc.setFontSize(14);
  doc.setLineHeightFactor(1);
  if (!customSection) return;

  await doc.html(
    `<div id="editor-container">
       <div style="width:${width - 30}px; font-size:16px;">
         ${customSection}
       </div>
     </div>`,
    {
      x: x + 15,
      y: y + 15,
      width: width - 30,
    }
  );
};

const pdfGenerationCustomSectionLine = async (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  customSection1: string | null,
  customSection2: string | null
) => {
  doc.setFontSize(7);
  doc.setLineHeightFactor(1);

  if (customSection1) {
    await doc.html(
      `<div style="font-size:16px" id="editor-container"> <div style="width:${width / 2 - 30}px">${customSection1}</div></div>`,
      {
        x: x + 15,
        y: y + 15,
        width: width / 2 - 30,
      }
    );
  }

  if (customSection2) {
    await doc.html(
      `<div style="font-size:16px" id="editor-container"> <div style="width:${width / 2 - 30}px">${customSection2}</div></div>`,
      {
        x: width / 2 + 15,
        y: y + 15,
        width: width / 2 - 30,
      }
    );
  }
};

const pdfGenerationModelLines = async (
  doc: jsPDF,
  customAppearance: CondensedCustomPdfAppearance,
  eventName: string,
  qrCodeImage: string | null,
  qrCodeId: string | null,
  logoImage: string,
  informations: string,
  customImage?: string
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tierY = pageHeight / 3;
  let y = 0;

  for (
    let index = 0;
    index < customAppearance.pdfSectionsOrder.length;
    index++
  ) {
    switch (customAppearance.pdfSectionsOrder[index]) {
      case "qrCode":
        await pdfGenerationQrCodeLine(
          doc,
          0,
          y,
          pageWidth,
          tierY,
          customAppearance,
          informations,
          eventName,
          qrCodeImage,
          qrCodeId,
          logoImage,
          customImage
        );
        y += tierY;
        break;
      case "conditions":
        pdfGenerationConditionsLine(doc, 0, y, pageWidth, customAppearance);
        y += tierY;
        break;
      case "customSection1":
        await pdfGenerationCustomSectionLine(
          doc,
          0,
          y,
          pageWidth,
          customAppearance.customSection1,
          customAppearance.customSection2
        );
        y += tierY;
        break;
      default:
        break;
    }
  }

  doc.setDrawColor(0);
  // @ts-expect-error this fonction isn't fully built by ts
  doc.setLineDash([3, 3], 0);
  doc.line(0, tierY, pageWidth, tierY);
  doc.line(0, 2 * tierY, pageWidth, 2 * tierY);
};

const pdfGenerationModelQuarter = async (
  doc: jsPDF,
  customAppearance: CondensedCustomPdfAppearance,
  eventName: string,
  qrCodeImage: string | null,
  qrCodeId: string | null,
  logoImage: string,
  informations: string,
  customImage?: string
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;
  const centerY = pageHeight / 2;

  for (
    let index = 0;
    index < customAppearance.pdfSectionsOrder.length;
    index++
  ) {
    const x = index % 2 === 0 ? 0 : centerX;
    const y = index < 2 ? 0 : centerY;
    switch (customAppearance.pdfSectionsOrder[index]) {
      case "qrCode":
        await pdfGenerationQrCodeQuarter(
          doc,
          x,
          y,
          centerX,
          centerY,
          customAppearance,
          informations,
          eventName,
          qrCodeImage,
          qrCodeId,
          logoImage,
          customImage
        );
        break;
      case "conditions":
        pdfGenerationConditionsQuarter(doc, x, y, pageWidth, customAppearance);
        break;
      case "customSection1":
        await pdfGenerationCustomSectionQuarter(
          doc,
          x,
          y,
          centerX,
          customAppearance.customSection1
        );
        break;
      case "customSection2":
        await pdfGenerationCustomSectionQuarter(
          doc,
          x,
          y,
          centerX,
          customAppearance.customSection2
        );
        break;
      default:
        break;
    }
  }

  doc.setDrawColor(0);
  // @ts-expect-error this fonction isn't fully built by ts
  doc.setLineDash([3, 3], 0);
  doc.line(centerX, 0, centerX, pageHeight);
  doc.line(0, centerY, pageWidth, centerY);
};

const pdfGenerationModelMixte = async (
  doc: jsPDF,
  customAppearance: CondensedCustomPdfAppearance,
  eventName: string,
  qrCodeImage: string | null,
  qrCodeId: string | null,
  logoImage: string,
  informations: string,
  customImage?: string
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;
  const tierY = pageHeight / 3;
  const y = customAppearance.pdfSectionsOrder[0] === "qrCode" ? tierY : 0;
  let x = 0;
  await pdfGenerationQrCodeLine(
    doc,
    0,
    customAppearance.pdfSectionsOrder[0] === "qrCode" ? 0 : 2 * tierY,
    pageWidth,
    tierY,
    customAppearance,
    informations,
    eventName,
    qrCodeImage,
    qrCodeId,
    logoImage,
    customImage
  );
  for (
    let index = 0;
    index < customAppearance.pdfSectionsOrder.length;
    index++
  ) {
    switch (customAppearance.pdfSectionsOrder[index]) {
      case "conditions":
        pdfGenerationConditionsQuarter(doc, x, y, pageWidth, customAppearance);
        x = centerX;
        break;
      case "customSection1":
        await pdfGenerationCustomSectionQuarter(
          doc,
          x,
          y,
          centerX,
          customAppearance.customSection1
        );
        x = centerX;
        break;
      default:
        break;
    }
  }

  doc.setDrawColor(0);
  // @ts-expect-error this fonction isn't fully built by ts
  doc.setLineDash([3, 3], 0);
  doc.line(centerX, y, centerX, y + 2 * tierY);
  doc.line(
    0,
    customAppearance.pdfSectionsOrder[0] === "qrCode" ? tierY : 2 * tierY,
    pageWidth,
    customAppearance.pdfSectionsOrder[0] === "qrCode" ? tierY : 2 * tierY
  );
};

export const generatePdfPage = async (
  doc: jsPDF,
  customAppearance: CondensedCustomPdfAppearance,
  eventName: string,
  qrCodeImage: string | null,
  qrCodeId: string | null,
  logoImage: string,
  informations: string,
  customImage?: string,
  backgroundImage?: string
) => {
  const hasBackgroundImage = !!backgroundImage;
  const currentOpacity = (customAppearance.opacity ?? 100) / 100;
  const backgroundColor = hexaToRgb(customAppearance.backgroundColor);
  doc.setFillColor(backgroundColor.r, backgroundColor.g, backgroundColor.b);
  doc.setGState(new GState({ opacity: hasBackgroundImage ? 1 : currentOpacity }));
  doc.rect(
    0,
    0,
    doc.internal.pageSize.width,
    doc.internal.pageSize.height,
    "F"
  );
  doc.setGState(new GState({ opacity: 1 }));

  if (hasBackgroundImage) {
    doc.setGState(new GState({ opacity: currentOpacity }));
    displayPictureFill(
      doc,
      backgroundImage,
      0,
      0,
      doc.internal.pageSize.getWidth(),
      doc.internal.pageSize.getHeight()
    );
    doc.setGState(new GState({ opacity: 1 }));
  }

  if (customAppearance.model === "fourQuarters") {
    await pdfGenerationModelQuarter(
      doc,
      customAppearance,
      eventName,
      qrCodeImage,
      qrCodeId,
      logoImage,
      informations,
      customImage
    );
  }
  if (customAppearance.model === "horizontal") {
    await pdfGenerationModelLines(
      doc,
      customAppearance,
      eventName,
      qrCodeImage,
      qrCodeId,
      logoImage,
      informations,
      customImage
    );
  }
  if (customAppearance.model === "mix") {
    await pdfGenerationModelMixte(
      doc,
      customAppearance,
      eventName,
      qrCodeImage,
      qrCodeId,
      logoImage,
      informations,
      customImage
    );
  }
};

export const generateTicketBadgePdfArrayBuffer = async (
  customAppearance: CondensedCustomPdfAppearance | null,
  eventName: string,
  qrCodeImage: string | null,
  qrCodeId: string | null,
  informations: string,
  logoImage: string,
  backgroundImage?: string,
  customImage?: string
): Promise<ArrayBuffer> => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });
  const customAppearanceUsed: CondensedCustomPdfAppearance =
    customAppearance ?? defaultCondensedCustomPdfAppearanceValues;

  await generatePdfPage(
    doc,
    customAppearanceUsed,
    eventName,
    qrCodeImage,
    qrCodeId,
    logoImage,
    informations,
    customImage,
    backgroundImage
  );
  return doc.output("arraybuffer");
};

export type PdfFinalisationData = {
  userName: string;
  qrCodeId: string;
  qrCodeUrl: string;
  qrCodeImage: PDFImage | null;
  qrWidth: number;
  qrHeight: number;
  nameFont: PDFFont;
};

const pdfEditionQrCodeQuarter = async (
  page: PDFPage,
  x: number,
  y: number,
  width: number,
  pageHeight: number,
  data: PdfFinalisationData,
  color: string
) => {
  const rgbColor = hexaToRgb(color);
  const { qrImage, qrId, name } = FOUR_QUARTERS_PDF_LAYOUT;
  if (data.qrCodeImage)
    page.drawImage(data.qrCodeImage, {
      x: x + (width - qrImage.size) / 2,
      y: pageHeight - (y + qrImage.offsetY + qrImage.size),
      width: qrImage.size,
      height: qrImage.size,
    });
  page.drawText(data.qrCodeId, {
    x: x + qrId.offsetX,
    y: pageHeight - (y + qrId.offsetY),
    size: qrId.fontSize,
    color: rgb(rgbColor.r / 255, rgbColor.g / 255, rgbColor.b / 255),
  });
  page.drawText(data.userName, {
    x: x + name.offsetX,
    y: pageHeight - (y + name.offsetY),
    size: name.fontSize,
    font: data.nameFont,
    color: rgb(rgbColor.r / 255, rgbColor.g / 255, rgbColor.b / 255),
  });
};

const pdfEditionQrCodeLine = async (
  page: PDFPage,
  x: number,
  y: number,
  pageHeight: number,
  data: PdfFinalisationData,
  color: string
) => {
  const rgbColor = hexaToRgb(color);
  const { qrImage, qrId, name } = LINE_PDF_LAYOUT;
  if (data.qrCodeImage)
    page.drawImage(data.qrCodeImage, {
      x: x + qrImage.offsetX,
      y: pageHeight - (y + qrImage.offsetY + qrImage.size),
      width: qrImage.size,
      height: qrImage.size,
    });
  page.drawText(data.qrCodeId, {
    x: x + qrId.offsetX,
    y: pageHeight - (y + qrId.offsetY),
    size: qrId.fontSize,
    color: rgb(rgbColor.r / 255, rgbColor.g / 255, rgbColor.b / 255),
  });
  page.drawText(data.userName, {
    x: x + name.offsetX,
    y: pageHeight - (y + name.offsetY),
    size: name.fontSize,
    font: data.nameFont,
    color: rgb(rgbColor.r / 255, rgbColor.g / 255, rgbColor.b / 255),
  });
};

const pdfEditionModelLines = async (
  page: PDFPage,
  customAppearance: CondensedCustomPdfAppearance,
  pageHeight: number,
  data: PdfFinalisationData,
  color: string
) => {
  const tierY = pageHeight / 3;

  let y = 0;

  for (
    let index = 0;
    index < customAppearance.pdfSectionsOrder.length;
    index++
  ) {
    if (customAppearance.pdfSectionsOrder[index] == "qrCode") {
      await pdfEditionQrCodeLine(page, 0, tierY * y, pageHeight, data, color);
      break;
    }
    if (customAppearance.pdfSectionsOrder[index] != "customSection2") y++;
  }
};

const pdfEditionModelQuarter = async (
  page: PDFPage,
  customAppearance: CondensedCustomPdfAppearance,
  pageHeight: number,
  pageWidth: number,
  data: PdfFinalisationData,
  color: string
) => {
  const centerX = pageWidth / 2;
  const centerY = pageHeight / 2;
  for (
    let index = 0;
    index < customAppearance.pdfSectionsOrder.length;
    index++
  ) {
    if (customAppearance.pdfSectionsOrder[index] == "qrCode") {
      await pdfEditionQrCodeQuarter(
        page,
        index % 2 === 0 ? 0 : centerX,
        index < 2 ? 0 : centerY,
        centerX,
        pageHeight,
        data,
        color
      );
      break;
    }
  }
};

const pdfEditionModelMixte = async (
  page: PDFPage,
  customAppearance: CondensedCustomPdfAppearance,
  pageHeight: number,
  data: PdfFinalisationData,
  color: string
) => {
  const tierY = pageHeight / 3;
  await pdfEditionQrCodeLine(
    page,
    0,
    customAppearance.pdfSectionsOrder[0] === "qrCode" ? 0 : 2 * tierY,
    pageHeight,
    data,
    color
  );
};

export const finalisePdf = async (
  pdfArrayBuffer: ArrayBuffer,
  customAppearance: CondensedCustomPdfAppearance,
  details: {
    userName: string;
    qrCodeId: string;
    qrCodeUrl: string;
  }[]
): Promise<Blob> => {
  const pdfDoc = await PDFDocument.load(pdfArrayBuffer);

  const finalPdf = await PDFDocument.create();
  const nameFont = await finalPdf.embedFont(StandardFonts.HelveticaBold);
  // The template page (and its images) is embedded once as a form XObject and
  // drawn on every page, so N titles weigh one template plus their QR codes.
  const templatePage = pdfDoc.getPage(0);
  const { width, height } = templatePage.getSize();
  const embeddedTemplate = await finalPdf.embedPage(templatePage);

  for (const detail of details) {
    const imageBytes = await fetch(detail.qrCodeUrl).then((res) =>
      res.arrayBuffer()
    );
    const image = await finalPdf.embedPng(imageBytes);
    const { width: widthQR, height: heightQR } = image.scale(1);
    const page = finalPdf.addPage([width, height]);
    page.drawPage(embeddedTemplate, { x: 0, y: 0, width, height });

    if (customAppearance.model === "fourQuarters") {
      await pdfEditionModelQuarter(
        page,
        customAppearance,
        height,
        width,
        {
          ...detail,
          qrCodeImage: image,
          qrWidth: widthQR,
          qrHeight: heightQR,
          nameFont,
        },
        customAppearance.textColor
      );
    }
    if (customAppearance.model === "horizontal") {
      await pdfEditionModelLines(
        page,
        customAppearance,
        height,
        {
          ...detail,
          qrCodeImage: image,
          qrWidth: widthQR,
          qrHeight: heightQR,
          nameFont,
        },
        customAppearance.textColor
      );
    }
    if (customAppearance.model === "mix") {
      await pdfEditionModelMixte(
        page,
        customAppearance,
        height,
        {
          ...detail,
          qrCodeImage: image,
          qrWidth: widthQR,
          qrHeight: heightQR,
          nameFont,
        },
        customAppearance.textColor
      );
    }
  }

  const pdfBytes = await finalPdf.save();
  return new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
};
